import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ReceiptIndianRupee } from "lucide-react";
import { API, api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Billing details used on GST tax invoices. */
export const BillingDetails = () => {
  const [f, setF] = useState({ legal_name: "", address: "", state: "", gstin: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/me/billing").then(({ data }) => setF((s) => ({ ...s, ...data }))).catch(() => {}); }, []);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/me/billing", { ...f, gstin: f.gstin || null, legal_name: f.legal_name || null }); toast.success("Billing details saved"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section className="border border-border bg-card p-7" data-testid="billing-section">
      <p className="label-caps">Billing details · for GST tax invoices</p>
      <p className="text-sm text-muted-foreground mt-2">Used on the tax invoice issued when you pay a case subscription fee. Karnataka → CGST + SGST; other states → IGST.</p>
      <form onSubmit={save} className="mt-5 grid gap-3 sm:grid-cols-2">
        <Input placeholder="Legal / firm name (optional)" value={f.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} className="rounded-none h-10" data-testid="billing-name-input" />
        <Input placeholder="GSTIN (optional)" value={f.gstin || ""} onChange={(e) => set("gstin", e.target.value.toUpperCase())} className="rounded-none h-10" data-testid="billing-gstin-input" maxLength={15} />
        <Input required placeholder="Billing address" value={f.address || ""} onChange={(e) => set("address", e.target.value)} className="rounded-none h-10 sm:col-span-2" data-testid="billing-address-input" />
        <Input required placeholder="State (e.g. Karnataka)" value={f.state || ""} onChange={(e) => set("state", e.target.value)} className="rounded-none h-10" data-testid="billing-state-input" />
        <Button type="submit" disabled={busy} className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-10" data-testid="billing-save-button">{busy ? "Saving…" : "Save"}</Button>
      </form>
    </section>
  );
};

/** List of GST tax invoices issued to the signed-in payer (or all, for the owner). */
export const TaxInvoices = ({ caseId }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/tax-invoices", { params: caseId ? { case_id: caseId } : {} }).then(({ data }) => setRows(data)).catch(() => {}); }, [caseId]);
  if (!rows.length) return null;
  return (
    <section className="border border-border bg-card" data-testid="tax-invoices-section">
      <div className="px-6 py-4 border-b border-border flex items-center gap-2"><ReceiptIndianRupee className="h-4 w-4" /><p className="label-caps">Tax invoices (GST)</p></div>
      {rows.map((t) => (
        <div key={t.id} className="px-6 py-3 border-b border-border last:border-0 flex flex-wrap items-center gap-3 text-sm" data-testid={`tax-invoice-${t.id}`}>
          <span className="font-mono text-xs">{t.number}</span>
          <span className="text-muted-foreground">{fmtDate(t.date)} · {t.case_ref} · {t.fee_note}</span>
          <span className="ml-auto font-medium">INR {t.tax.total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
          <span className="text-[11px] text-muted-foreground">{t.tax.supply === "intra" ? "CGST 9% + SGST 9%" : "IGST 18%"}</span>
          <a href={`${API}/tax-invoices/${t.id}.pdf`} target="_blank" rel="noreferrer" data-testid={`tax-invoice-pdf-${t.id}`}
            className="flex items-center gap-1 text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200"><Download className="h-3.5 w-3.5" /> PDF</a>
        </div>
      ))}
    </section>
  );
};
